import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as timeIntervalsService from '../services/timeIntervalsService';
import type { ThunkAPI } from '../store';
import { finaliseLogout } from '../store/thunks';
import { ApiTimeInterval } from '../types/TimeInterval';

type FetchResult = { timeIntervals: ApiTimeInterval[] } | undefined;

export interface TimeIntervalsState {
  timeIntervalsById: Record<string, ApiTimeInterval>;
  isFetching: boolean;
  error?: SerializedError;
}

const initialState: TimeIntervalsState = {
  timeIntervalsById: {},
  isFetching: false,
  error: undefined,
};

export const fetchTimeIntervals = createAsyncThunk<FetchResult, void, ThunkAPI>(
  'timeIntervals/fetch',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const customerId = state.client.activeCustomer?.id;

    if (!customerId) {
      return;
    }

    const res = await timeIntervalsService.getTimeIntervals(customerId);

    return { timeIntervals: res };
  },
);

const timeIntervalsSlice = createSlice({
  name: 'timeIntervals',
  initialState,
  reducers: {
    timeIntervalsReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeIntervals.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      })
      .addCase(
        fetchTimeIntervals.fulfilled,
        (state, action: PayloadAction<FetchResult>) => {
          if (!action.payload) {
            return;
          }
          const timeIntervalsById: Record<string, ApiTimeInterval> = {};
          action.payload.timeIntervals.forEach((ti) => {
            timeIntervalsById[ti.id] = ti;
          });
          state.timeIntervalsById = timeIntervalsById;
          state.isFetching = false;
          state.error = undefined;
        },
      )
      .addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const { timeIntervalsReset } = timeIntervalsSlice.actions;

export default timeIntervalsSlice.reducer;
